import { useState, useEffect, useRef } from 'react';

const solutions = [
  {
    id: 'visibility',
    number: '01',
    icon: 'ri-eye-line',
    title: 'See Every Fee. Every Markup. Every Time.',
    description:
      'You agreed to one rate. The fine print says otherwise. We give you a complete, readable breakdown of every cost — no buried fees, no hidden markups, ever.',
    tag: 'Transparency',
    link: '/solutions',
  },
  {
    id: 'omnichannel',
    number: '02',
    icon: 'ri-store-2-line',
    title: 'One System. No Blind Spots.',
    description:
      'Every transaction across in-store, online, and mobile flows into one clear view of your revenue. No gaps. No guesswork.',
    tag: 'Core Infrastructure',
    link: '/solutions',
  },
  {
    id: 'payouts',
    number: '03',
    icon: 'ri-bank-line',
    title: 'Get Paid When You Should, Not When They Decide.',
    description:
      'Random holds and delayed deposits are not a processing quirk. They are a cash flow problem. We give you consistent, predictable funding you can actually plan around.',
    tag: 'Cash Flow',
    link: '/solutions',
  },
  {
    id: 'fraud',
    number: '04',
    icon: 'ri-shield-check-line',
    title: 'Block Fraud. Not Customers.',
    description:
      'Fraud tools that flag legitimate transactions cost you more than the fraud itself. We catch real threats without adding friction to good sales.',
    tag: 'Security',
    link: '/solutions',
  },
  {
    id: 'highrisk',
    number: '05',
    icon: 'ri-shield-star-line',
    title: 'Approved where others fail. Built-in chargeback protection.',
    description:
      'Specialized merchant accounts for hard-to-place industries with robust chargeback mitigation tools.',
    tag: 'High-Risk',
    link: '/solutions',
  },
  {
    id: 'cashdiscount',
    number: '06',
    icon: 'ri-money-dollar-circle-line',
    title: 'No processing fees. Stay compliant.',
    description:
      'Legally pass the cost of processing to customers paying with cards, eliminating your processing fees entirely.',
    tag: 'Cost Reduction',
    link: '/solutions',
  },
];

const STAGGER_DELAYS = [0, 120, 240, 360, 480, 600];

export default function SolutionsGrid() {
  const [active, setActive] = useState<number | null>(null);
  const [visibleCards, setVisibleCards] = useState<boolean[]>([false, false, false, false, false, false]);
  const [headerVisible, setHeaderVisible] = useState(false);

  const sectionRef = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;
            setHeaderVisible(true);
            solutions.forEach((_, i) => {
              setTimeout(() => {
                setVisibleCards((prev) => {
                  const next = [...prev];
                  next[i] = true;
                  return next;
                });
              }, 200 + STAGGER_DELAYS[i]);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="solutions" className="bg-[#F9F9F7] pt-28" ref={sectionRef}>
      <div className="max-w-[1400px] mx-auto px-5 md:px-[75px]">

        {/* Section Header */}
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-end mb-20"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 600ms ease, transform 600ms ease',
          }}
        >
          <div>
            <p
              className="text-[#a8894a] text-[10px] uppercase tracking-[0.25em] font-medium mb-5"
              style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
            >
              Capabilities
            </p>
            <h2
              className="text-[#061e45] text-4xl md:text-5xl lg:text-[54px] font-light leading-[1.1]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Where Your Money Is Going And Why It Matters
            </h2>
          </div>
          <blockquote
            className="border-l-2 border-[#d3b366] pl-6 md:pl-8"
          >
            <p
              className="text-[#061e45]/70 text-[15px] md:text-[17px] lg:text-[19px] leading-relaxed"
              style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
            >
              We start with the parts no one shows you, fees, markups, and timing gaps, and then we fix them.
            </p>
          </blockquote>
        </div>

        {/* Grid — matches The Process style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1A1A1A]/10">
          {solutions.map((sol, i) => (
            <div
              key={sol.id}
              onClick={() => setActive(active === i ? null : i)}
              className={`group p-10 cursor-pointer transition-all duration-300 ${
                active === i ? 'bg-white' : 'bg-[#F9F9F7] hover:bg-white'
              }`}
              style={{
                opacity: visibleCards[i] ? 1 : 0,
                transform: visibleCards[i] ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 600ms ease ${STAGGER_DELAYS[i]}ms, transform 600ms cubic-bezier(0.25,0.46,0.45,0.94) ${STAGGER_DELAYS[i]}ms, background-color 300ms ease`,
              }}
            >
              {/* Icon row */}
              <div className="flex items-center justify-between mb-8">
                <div className={`w-14 h-14 flex items-center justify-center rounded-full transition-all duration-300 ${
                  active === i ? 'bg-[#122852]' : 'bg-[#1A1A1A]/6 group-hover:bg-[#122852]/10'
                }`}>
                  <i className={`${sol.icon} text-2xl transition-colors duration-300 ${
                    active === i ? 'text-[#d3b366]' : 'text-[#1A1A1A]/35 group-hover:text-[#122852]'
                  }`} />
                </div>
              </div>

              {/* Tag */}
              <p
                className={`text-[10px] uppercase tracking-[0.2em] font-medium mb-3 transition-colors duration-300 ${
                  active === i ? 'text-[#a8894a]' : 'text-[#1A1A1A]/30 group-hover:text-[#a8894a]/70'
                }`}
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                {sol.tag}
              </p>

              {/* Title */}
              <h3
                className={`text-xl font-medium leading-snug mb-4 transition-colors duration-300 ${
                  active === i ? 'text-[#061e45]' : 'text-[#1A1A1A]/70 group-hover:text-[#061e45]'
                }`}
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                {sol.title}
              </h3>

              {/* Body */}
              <p
                className="text-[#1A1A1A]/50 text-[15px] leading-relaxed mb-6"
                style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
              >
                {sol.description}
              </p>

              {/* Learn more link — visible when active */}
              <div
                className="overflow-hidden transition-all duration-300"
                style={{ maxHeight: active === i ? '40px' : '0px', opacity: active === i ? 1 : 0 }}
              >
                <a
                  href={sol.link}
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-medium text-[#a8894a] hover:text-[#122852] transition-colors duration-200 cursor-pointer"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn more
                  <i className="ri-arrow-right-line text-sm" />
                </a>
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* CTA Band — Frosted Glass over hand-signing-document */}
      <div className="relative mt-[50px] overflow-hidden">
        {/* Full-bleed background */}
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: 'url(/images/cta-wipe-01.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-[#122852]/65" />

        {/* Frosted glass card */}
        <div className="relative z-10 px-5 md:px-[75px] py-16 flex items-center justify-center">
          <div
            className="w-full max-w-[1100px] grid grid-cols-1 lg:grid-cols-2 gap-0"
            style={{
              background: 'rgba(255,255,255,0.08)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.15)',
            }}
          >
            <div className="p-6 md:p-12 lg:border-r border-white/10">
              <p
                className="text-[#d3b366] text-[10px] uppercase tracking-[0.25em] font-medium mb-4"
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                Get Started
              </p>
              <h3
                className="text-white text-2xl md:text-3xl font-light leading-snug"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                You&apos;re already processing payments. The question is how much are you losing?
              </h3>
            </div>
            <div className="p-6 md:p-12 flex flex-col justify-center gap-4">
              <a
                href="/contact"
                className="whitespace-nowrap inline-flex items-center justify-center gap-3 text-[12px] uppercase tracking-[0.18em] font-semibold px-8 py-4 bg-[#d3b366] text-[#122852] hover:bg-white hover:text-[#122852] transition-all duration-200 cursor-pointer"
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                Talk to Us
                <i className="ri-arrow-right-line" />
              </a>
              <a
                href="/solutions"
                className="whitespace-nowrap inline-flex items-center justify-center gap-3 text-[12px] uppercase tracking-[0.18em] font-medium px-8 py-4 border border-white/25 text-white/70 hover:border-white/50 hover:text-white transition-all duration-200 cursor-pointer"
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                Explore Solutions
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}